import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useAthlete } from '@/context/AthleteContext';
import { 
  FileText, Calendar, Target, Dumbbell, Video, MessageSquare, 
  Award, TrendingUp, ChevronLeft, ChevronRight,
  CheckCircle, XCircle, Clock, RefreshCw
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Skills organized by category
const SKILL_CATEGORIES = [
  {
    category: 'TECHNICAL',
    color: 'text-emerald-400',
    bgColor: 'bg-emerald-500/10',
    skills: [
      { key: 'ball_control', label: 'Ball Control' },
      { key: 'passing', label: 'Passing' },
      { key: 'dribbling', label: 'Dribbling' },
      { key: 'first_touch', label: 'First Touch' },
      { key: 'finishing', label: 'Finishing' },
      { key: 'shooting', label: 'Shooting' },
    ]
  },
  {
    category: 'PHYSICAL',
    color: 'text-blue-400',
    bgColor: 'bg-blue-500/10',
    skills: [
      { key: 'speed', label: 'Speed' },
      { key: 'acceleration', label: 'Acceleration' },
      { key: 'agility', label: 'Agility' },
      { key: 'coordination', label: 'Coordination' },
      { key: 'balance', label: 'Balance' },
      { key: 'explosiveness', label: 'Explosiveness' },
      { key: 'endurance', label: 'Endurance' },
    ]
  },
  {
    category: 'TACTICAL / IQ',
    color: 'text-yellow-400',
    bgColor: 'bg-yellow-500/10',
    skills: [
      { key: 'positioning', label: 'Positioning' },
      { key: 'decision_making', label: 'Decision Making' },
      { key: 'vision', label: 'Vision' },
      { key: 'game_awareness', label: 'Game Awareness' },
      { key: 'reaction_time', label: 'Reaction Time' },
    ]
  }
];

const ParentMonthlyReport = () => {
  const { getAuthHeaders } = useAuth();
  const { athlete } = useAthlete();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  const fetchReport = async (month, year) => {
    if (!athlete) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/parent/monthly-report`, {
        params: { month, year, athlete_id: athlete.id },
        headers: getAuthHeaders()
      });
      setReport(res.data);
    } catch (error) {
      console.error('Failed to fetch report:', error);
      setReport(null);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (athlete) {
      fetchReport(selectedMonth, selectedYear);
    }
  }, [selectedMonth, selectedYear, athlete]);

  const handlePrevMonth = () => {
    if (selectedMonth === 1) {
      setSelectedMonth(12);
      setSelectedYear(selectedYear - 1);
    } else {
      setSelectedMonth(selectedMonth - 1);
    }
  };

  const handleNextMonth = () => {
    const now = new Date();
    const isCurrentMonth = selectedMonth === now.getMonth() + 1 && selectedYear === now.getFullYear();
    if (isCurrentMonth) return;
    
    if (selectedMonth === 12) {
      setSelectedMonth(1);
      setSelectedYear(selectedYear + 1);
    } else {
      setSelectedMonth(selectedMonth + 1);
    }
  };

  const getMonthName = (month) => {
    const months = ['January', 'February', 'March', 'April', 'May', 'June', 
                    'July', 'August', 'September', 'October', 'November', 'December'];
    return months[month - 1];
  };

  const getSkillLabel = (skill) => {
    const labels = {
      // TECHNICAL
      'ball_control': 'Ball Control',
      'passing': 'Passing',
      'dribbling': 'Dribbling',
      'first_touch': 'First Touch',
      'finishing': 'Finishing',
      'shooting': 'Shooting',
      // PHYSICAL
      'speed': 'Speed',
      'acceleration': 'Acceleration',
      'agility': 'Agility',
      'coordination': 'Coordination',
      'balance': 'Balance',
      'explosiveness': 'Explosiveness',
      'endurance': 'Endurance',
      // TACTICAL / IQ
      'positioning': 'Positioning',
      'decision_making': 'Decision Making',
      'vision': 'Vision',
      'game_awareness': 'Game Awareness',
      'reaction_time': 'Reaction Time',
      // Legacy
      'passing_receiving': 'Passing & Receiving',
      'defending': 'Defending'
    };
    return labels[skill] || skill?.replace('_', ' ');
  };

  const getTechDevStatus = (status) => {
    const statusMap = {
      'Focus': { label: 'Focus', color: 'text-red-400 bg-red-500/20' },
      'Developing': { label: 'Developing', color: 'text-yellow-400 bg-yellow-500/20' },
      'Proficient': { label: 'Proficient', color: 'text-green-400 bg-green-500/20' },
      // Legacy mappings
      'Improving': { label: 'Developing', color: 'text-yellow-400 bg-yellow-500/20' },
      'Strong': { label: 'Proficient', color: 'text-green-400 bg-green-500/20' }
    };
    return statusMap[status] || { label: status || 'N/A', color: 'text-muted-foreground bg-muted' };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="parent-monthly-report">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl uppercase tracking-tight">Monthly Report</h1>
          <p className="text-muted-foreground">Track your athlete's progress</p>
        </div>
        
        {/* Month Navigation */}
        <div className="flex items-center gap-2 bg-card border border-border/40 rounded-sm p-2">
          <Button variant="ghost" size="sm" onClick={handlePrevMonth}>
            <ChevronLeft size={18} />
          </Button>
          <div className="px-4 text-center min-w-[160px]">
            <p className="font-heading text-lg">{getMonthName(selectedMonth)}</p>
            <p className="text-xs text-muted-foreground">{selectedYear}</p>
          </div>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={handleNextMonth}
            disabled={selectedMonth === new Date().getMonth() + 1 && selectedYear === new Date().getFullYear()}
          >
            <ChevronRight size={18} />
          </Button>
        </div>
      </div>

      {!report ? (
        <div className="bg-card border border-border/40 p-12 rounded-sm text-center">
          <FileText className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
          <p className="text-muted-foreground">No data available for this period</p>
        </div>
      ) : (
        <>
          {/* Athlete & Coach Info */}
          <div className="bg-card border border-border/40 p-6 rounded-sm">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center">
                  <span className="text-primary font-bold text-xl">{report.athlete?.name?.[0]}</span>
                </div>
                <div>
                  <h2 className="font-heading text-xl">{report.athlete?.name}</h2>
                  <p className="text-sm text-muted-foreground">
                    {report.athlete?.position} • {report.athlete?.age} years • {report.athlete?.sport}
                  </p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-sm text-muted-foreground">Coach</p>
                <p className="font-medium">{report.coach?.name}</p>
                <p className="text-xs text-primary">{report.coach?.business_name}</p>
              </div>
            </div>
          </div>

          {/* Sessions Summary */}
          <div className="bg-card border border-border/40 p-6 rounded-sm">
            <div className="flex items-center gap-3 mb-4">
              <Calendar className="text-primary" size={22} />
              <h3 className="font-heading text-lg uppercase">Sessions This Month</h3>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center p-4 bg-green-500/10 border border-green-500/30 rounded-sm">
                <div className="flex items-center justify-center gap-2 mb-1">
                  <CheckCircle className="text-green-400" size={18} />
                  <p className="text-2xl font-heading text-green-400">{report.sessions?.completed || 0}</p>
                </div>
                <p className="text-xs text-muted-foreground">Completed</p>
              </div>
              <div className="text-center p-4 bg-blue-500/10 border border-blue-500/30 rounded-sm">
                <div className="flex items-center justify-center gap-2 mb-1">
                  <Clock className="text-blue-400" size={18} />
                  <p className="text-2xl font-heading text-blue-400">{report.sessions?.scheduled || 0}</p>
                </div>
                <p className="text-xs text-muted-foreground">Scheduled</p>
              </div>
              <div className="text-center p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-sm">
                <div className="flex items-center justify-center gap-2 mb-1">
                  <RefreshCw className="text-yellow-400" size={18} />
                  <p className="text-2xl font-heading text-yellow-400">{report.sessions?.rescheduled || 0}</p>
                </div>
                <p className="text-xs text-muted-foreground">Rescheduled</p>
              </div>
              <div className="text-center p-4 bg-red-500/10 border border-red-500/30 rounded-sm">
                <div className="flex items-center justify-center gap-2 mb-1">
                  <XCircle className="text-red-400" size={18} />
                  <p className="text-2xl font-heading text-red-400">{report.sessions?.cancelled || 0}</p>
                </div>
                <p className="text-xs text-muted-foreground">Cancelled</p>
              </div>
            </div>
          </div>

          {/* Training & Skills */}
          <div className="grid md:grid-cols-2 gap-4">
            {/* Training Plan */}
            <div className="bg-card border border-border/40 p-6 rounded-sm">
              <div className="flex items-center gap-3 mb-4">
                <Target className="text-primary" size={22} />
                <h3 className="font-heading text-lg uppercase">Training Plan</h3>
              </div>
              {report.training?.plan_name ? (
                <div className="space-y-4">
                  <p className="font-medium">{report.training.plan_name}</p>
                  {report.training.objectives?.length > 0 && (
                    <div>
                      <p className="text-xs text-muted-foreground uppercase mb-2">Objectives</p>
                      <ul className="space-y-1">
                        {report.training.objectives.map((obj, idx) => (
                          <li key={idx} className="flex items-start gap-2 text-sm">
                            <CheckCircle className="text-primary mt-0.5 flex-shrink-0" size={14} />
                            <span>{obj}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {report.training.plan_notes && (
                    <div className="bg-muted/30 p-3 rounded-sm">
                      <p className="text-xs text-muted-foreground uppercase mb-1">Coach Notes</p>
                      <p className="text-sm italic">"{report.training.plan_notes}"</p>
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">No active training plan</p>
              )}
            </div>

            {/* Skills Focus */}
            <div className="bg-card border border-border/40 p-6 rounded-sm">
              <div className="flex items-center gap-3 mb-4">
                <Dumbbell className="text-primary" size={22} />
                <h3 className="font-heading text-lg uppercase">Skills Worked</h3>
              </div>
              {report.training?.skills_focus?.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {report.training.skills_focus.map((skill, idx) => (
                    <span 
                      key={idx} 
                      className="px-3 py-1.5 bg-primary/10 text-primary rounded-sm text-sm capitalize"
                    >
                      {getSkillLabel(skill)}
                    </span>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">No specific skills recorded</p>
              )}

              {report.training?.drills_worked?.length > 0 && (
                <div className="mt-4 pt-4 border-t border-border/40">
                  <p className="text-xs text-muted-foreground uppercase mb-2">
                    Drills ({report.training.drills_worked.length})
                  </p>
                  <div className="space-y-2 max-h-32 overflow-y-auto">
                    {report.training.drills_worked.map((drill, idx) => (
                      <div key={idx} className="flex items-center justify-between text-sm">
                        <span>{drill.name}</span>
                        <span className="text-xs text-muted-foreground capitalize">{drill.category}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Technical Development */}
          {report.technical_development && (
            <div className="bg-card border border-border/40 p-6 rounded-sm">
              <div className="flex items-center gap-3 mb-4">
                <TrendingUp className="text-primary" size={22} />
                <h3 className="font-heading text-lg uppercase">Technical Development</h3>
              </div>
              
              {/* Status Legend */}
              <div className="mb-4 pb-4 border-b border-border/40">
                <div className="flex flex-wrap items-center gap-4 text-sm">
                  <span className="text-muted-foreground">Status:</span>
                  <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-400"></span> Focus</span>
                  <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-yellow-400"></span> Developing</span>
                  <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-green-400"></span> Proficient</span>
                </div>
              </div>

              <div className="space-y-6">
                {SKILL_CATEGORIES.map((category) => (
                  <div key={category.category}>
                    <div className="flex items-center gap-3 mb-3">
                      <h4 className={`font-heading text-sm uppercase tracking-wider ${category.color}`}>
                        {category.category}
                      </h4>
                      <div className="flex-1 h-px bg-border"></div>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                      {category.skills.map((skill) => {
                        const data = report.technical_development[skill.key];
                        const statusInfo = getTechDevStatus(data?.status);
                        return (
                          <div key={skill.key} className={`text-center p-3 rounded-sm ${category.bgColor}`}>
                            <p className="text-xs text-muted-foreground mb-2">
                              {skill.label}
                            </p>
                            <span className={`text-xs px-2 py-1 rounded ${statusInfo.color}`}>
                              {statusInfo.label}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Coach Notes from Sessions */}
          {report.coach_notes?.length > 0 && (
            <div className="bg-card border border-border/40 p-6 rounded-sm">
              <div className="flex items-center gap-3 mb-4">
                <MessageSquare className="text-primary" size={22} />
                <h3 className="font-heading text-lg uppercase">Coach Feedback</h3>
              </div>
              <div className="space-y-3 max-h-64 overflow-y-auto">
                {report.coach_notes.map((note, idx) => (
                  <div key={idx} className="bg-muted/30 p-4 rounded-sm">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-xs text-primary font-medium">
                        {new Date(note.date).toLocaleDateString('en-US')}
                      </p>
                      {note.focus && (
                        <span className="text-xs text-muted-foreground">
                          Focus: {note.focus}
                        </span>
                      )}
                    </div>
                    <p className="text-sm">{note.note}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Milestones & Videos */}
          <div className="grid md:grid-cols-2 gap-4">
            {/* Milestones */}
            <div className="bg-card border border-border/40 p-6 rounded-sm">
              <div className="flex items-center gap-3 mb-4">
                <Award className="text-primary" size={22} />
                <h3 className="font-heading text-lg uppercase">Achievements</h3>
              </div>
              <div className="mb-4">
                <p className="text-3xl font-heading text-primary">{report.milestones?.total_sessions_all_time || 0}</p>
                <p className="text-xs text-muted-foreground">Total sessions completed</p>
              </div>
              <div className="grid grid-cols-2 gap-2">
                {report.milestones?.achievements?.map((milestone, idx) => (
                  <div 
                    key={idx}
                    className={`p-3 rounded-sm border ${
                      milestone.achieved 
                        ? 'bg-green-500/10 border-green-500/30' 
                        : 'bg-muted/20 border-border/40 opacity-60'
                    }`}
                  >
                    <p className={`text-sm font-medium ${milestone.achieved ? 'text-green-400' : ''}`}>
                      {milestone.name}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {milestone.achieved ? '✓ Achieved' : `${milestone.target} sessions`}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            {/* Videos */}
            <div className="bg-card border border-border/40 p-6 rounded-sm">
              <div className="flex items-center gap-3 mb-4">
                <Video className="text-primary" size={22} />
                <h3 className="font-heading text-lg uppercase">Video Analysis</h3>
              </div>
              <div className="text-center py-6">
                <p className="text-4xl font-heading text-primary">{report.videos_added || 0}</p>
                <p className="text-sm text-muted-foreground mt-1">Videos added this month</p>
              </div>
            </div>
          </div>

          {/* Report Footer */}
          <div className="text-center text-xs text-muted-foreground py-4 border-t border-border/40">
            <p>Report generated on {new Date(report.generated_at).toLocaleString('en-US')}</p>
            <p className="mt-1">{report.coach?.business_name} • Powered by Meybra Coach Platform</p>
          </div>
        </>
      )}
    </div>
  );
};

export default ParentMonthlyReport;
